import { useCallback, useEffect, useState } from 'react';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Check, Close, ContentCopy } from '@mui/icons-material';
import { Badge, Button, IconButton, Tooltip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { useGetProviderTranscriptionsQuery } from '../store/api';
import { formatDateTime, getStatusBadgeColor } from '../utils/formatting';

export default function TranscriptionsListPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const copy = useCopyToClipboard();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [perPage, setPerPage] = useState(() => {
    const perPageParam = searchParams.get('perPage');
    return perPageParam ? parseInt(perPageParam, 10) : 20;
  });
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    const initialFilters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key !== 'page' && key !== 'perPage') {
        initialFilters[key] = value;
      }
    });
    return initialFilters;
  });

  // Update URL when state changes
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Add page if not 1
    if (page !== 1) {
      newParams.set('page', page.toString());
    }

    // Add perPage if not 20
    if (perPage !== 20) {
      newParams.set('perPage', perPage.toString());
    }

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams, { replace: true });
  }, [page, perPage, filters, setSearchParams]);

  // Memoized filter change handler
  const handleFiltersChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  // Get URL for each row
  const getRowUrl = useCallback(
    (transcription: any) => {
      return providerId
        ? `/admin-v2/provider/${encodeURIComponent(providerId)}/transcriptions/${transcription.transcription_session_id}`
        : `/admin-v2/transcript/${transcription.transcription_session_id}`;
    },
    [providerId],
  );

  // Fetch provider's transcriptions with pagination and filtering
  const { data: transcriptionsData, isLoading: transcriptionsLoading } =
    useGetProviderTranscriptionsQuery(
      {
        providerId: providerId ?? '',
        page,
        perPage,
        ...filters,
      },
      {
        skip: !providerId,
      },
    );

  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">No provider ID specified.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const transcriptionsColumns = [
    {
      key: 'transcription_session_id',
      label: 'Session ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">{value}</span>
          <Tooltip title="Copy ID">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                copy(value, 'Session ID');
              }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'start_time',
      label: 'Started',
      render: (value: string) => (
        <span className="text-sm text-gray-900">{value ? formatDateTime(value) : 'N/A'}</span>
      ),
    },
    {
      key: 'chunk_count',
      label: 'Chunks',
      render: (value: number) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'notes_created',
      label: 'Notes',
      render: (value: number) => (
        <span className={`text-sm font-medium ${value === 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {value}
        </span>
      ),
    },
    {
      key: 'job_status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'is_closed',
      label: 'Closed',
      render: (value: boolean) =>
        value ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Close className="h-4 w-4 text-red-500" />
        ),
    },
    {
      key: 'end_time',
      label: 'Ended',
      render: (value: string | null) => (
        <span className="text-sm text-gray-500">{value ? formatDateTime(value) : 'N/A'}</span>
      ),
    },
  ];

  const filterConfigs = [
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'completed', label: 'Completed' },
        { value: 'processing', label: 'Processing' },
        { value: 'failed', label: 'Failed' },
        { value: 'pending', label: 'Pending' },
      ],
    },
    {
      key: 'encounterId',
      label: 'Encounter ID',
      type: 'text' as const,
    },
    {
      key: 'appointmentId',
      label: 'Appointment ID',
      type: 'text' as const,
    },
  ];

  return (
    <FilterableDataTable
      title="Transcriptions"
      columns={transcriptionsColumns as any}
      data={transcriptionsData?.items || []}
      total={transcriptionsData?.total}
      loading={transcriptionsLoading}
      page={page}
      perPage={perPage}
      filters={filterConfigs}
      onPageChange={setPage}
      onPerPageChange={setPerPage}
      onFiltersChange={handleFiltersChange}
      getRowUrl={getRowUrl}
      emptyMessage="No transcriptions to display"
    />
  );
}
